import { Alert } from '@mui/lab';
import { Box, InputLabel, Stack, TextField } from '@mui/material';
import CustomInput from 'components/custom-input/custom-input.component';
import FormError from 'components/form-error/form-error.component';
import IncButton from 'components/inc-button/inc-button.component';
import TimerLink from 'components/timer-link/timer-link.component';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { passwordlessAuthInitStartAsync } from 'redux/user/user.actions';
import Form from '../form/form.component';
import ContinueWithPassword from 'components/continue-with-password/continue-with-password.component';
import { usePasswordlessOAuthVerification } from 'pages/passwordless-activation/passwordless-activation.hooks';
import { selectPasswordlessOAuthResponse } from 'redux/user/user.selectors';
import { useEffect, useState } from 'react';

const OtpConfirmation = ({
  send,
  destination,
  isNewUser,
  method,
  identifier,
  handleDrawerToggle,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [resendError, setResendError] = useState();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ mode: 'onSubmit', reValidateMode: 'onBlur' });

  const passwordlessOAuthResponse = useSelector(
    selectPasswordlessOAuthResponse,
  );

  const { passwordlessOAuthVerify, passwordlessOAuthError } =
    usePasswordlessOAuthVerification();

  useEffect(() => {
    if (passwordlessOAuthResponse?.is_new_user) {
      handleDrawerToggle?.(false)();
      history.push('/m-login');
    }
  }, [passwordlessOAuthResponse, history, handleDrawerToggle]);

  const onSubmit = async ({ code }) => {
    await passwordlessOAuthVerify(code, method, identifier);
  };

  const handleResend = async () => {
    setResendError(undefined);
    const response = await dispatch(
      passwordlessAuthInitStartAsync({ identifier, method }),
    );
    if (!response.data) setResendError(response);
  };

  return (
    <Stack justifyContent="space-between" flex={1} minHeight="200px">
      <Box display="grid" rowGap={theme => theme.spacing(1)}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('passwordless.otpConfirmation.banner', { destination })}
        </Alert>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <CustomInput>
            <InputLabel htmlFor="code">
              {t('passwordless.otpConfirmation.enterCode')}
            </InputLabel>
            <TextField
              id="code"
              fullWidth
              size="small"
              placeholder={t('passwordless.otpConfirmation.enterCode')}
              error={!!errors.code}
              {...register('code', {
                required: t('register.errors.requiredField'),
              })}
            />
            {errors.code && <FormError errorMessage={errors.code.message} />}
          </CustomInput>
          {!!passwordlessOAuthError && (
            <FormError errorMessage={passwordlessOAuthError} />
          )}
          <IncButton
            type="submit"
            fullWidth
            disabled={isSubmitting}
            data-testid="OtpConfirmation.Button.Proceed"
          >
            {t('passwordless.otpConfirmation.proceed')}
          </IncButton>
        </Form>
        <TimerLink
          expiry={30}
          onClick={handleResend}
          label={t('passwordless.otpConfirmation.didntReceive')}
          disabled={resendError?.errorCode === 'RATE_LIMIT_EXCEEDED'}
        />
        {!!resendError && <FormError errorMessage={resendError.errorMessage} />}
      </Box>
      <ContinueWithPassword send={send} isNewUser={isNewUser} />
    </Stack>
  );
};

export default OtpConfirmation;
